import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Ticket } from '../types';
import { mobileTab, ticketKey, removeTicketFromQueue } from './workflow';
import { mergeTicketPayload } from './ticket-payload';

export type RealtimeStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface TicketState {
  tickets: Ticket[];
  snapshotReady: boolean;
  realtimeStatus: RealtimeStatus;
  setSnapshot(items: Ticket[]): void;
  mergeMany(items: Ticket[]): void;
  upsert(ticket: Ticket): void;
  remove(id: string): void;
  removeFromQueue(id: string): void;
  setRealtimeStatus(status: RealtimeStatus): void;
  find(id: string, runId?: string): Ticket | undefined;
}

const Context = createContext<TicketState | null>(null);

function mergeTickets(current: Ticket[], incoming: Ticket[]): Ticket[] {
  const byKey = new Map(current.map((ticket) => [ticketKey(ticket), ticket]));
  for (const ticket of incoming) {
    const key = ticketKey(ticket);
    const definedFields = Object.fromEntries(Object.entries(ticket).filter(([, value]) => value !== undefined));
    byKey.set(key, mergeTicketPayload(byKey.get(key) || {}, definedFields));
  }
  return [...byKey.values()];
}

export function TicketProvider({ children }: { children: ReactNode }) {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [snapshotReady, setSnapshotReady] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>('disconnected');
  const setSnapshot = useCallback((items: Ticket[]) => {
    setTickets(items);
    setSnapshotReady(true);
  }, []);
  const mergeMany = useCallback((items: Ticket[]) => setTickets((current) => mergeTickets(current, items)), []);
  const upsert = useCallback((ticket: Ticket) => setTickets((current) => mergeTickets(current, [ticket])), []);
  const remove = useCallback((id: string) => setTickets((current) => current.filter((ticket) => ticket.ticketId !== id && ticket._id !== id)), []);
  const removeFromQueue = useCallback((id: string) => setTickets((current) => removeTicketFromQueue(current, id)), []);
  const find = useCallback((id: string, runId = '') => tickets.find((item) => item.ticketId === id &&
    (runId ? item.runId === runId : mobileTab(item) !== 'completed')), [tickets]);
  const value = useMemo(() => ({
    tickets, snapshotReady, realtimeStatus, setSnapshot, mergeMany, upsert, remove, removeFromQueue, setRealtimeStatus, find,
  }), [tickets, snapshotReady, realtimeStatus, setSnapshot, mergeMany, upsert, remove, removeFromQueue, find]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useTickets() {
  const value = useContext(Context);
  if (!value) throw new Error('TicketProvider lipsește');
  return value;
}
